import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";

/**
 * Stripe checkout return handler.
 *
 * We poll GET /api/payments/status/{session_id} (unauthenticated per
 * playbook) up to 20 times with a 2-second interval. Once status becomes
 * "paid", we show the celebration state and route the user home.
 */
export default function BillingSuccess() {
    const [sp] = useSearchParams();
    const nav = useNavigate();
    const [state, setState] = useState({ status: "polling", tries: 0 });

    useEffect(() => {
        const sessionId = sp.get("session_id");
        if (!sessionId) {
            setState({ status: "error", detail: "Missing session id" });
            return;
        }
        let cancelled = false;
        let tries = 0;
        const poll = async () => {
            tries += 1;
            try {
                const { data } = await api.get(`/payments/status/${sessionId}`);
                if (cancelled) return;
                if (data.payment_status === "paid") {
                    setState({ status: "paid", tries });
                    return;
                }
                if (["failed", "expired"].includes(data.payment_status)) {
                    setState({ status: "failed", tries, detail: data.payment_status });
                    return;
                }
                if (tries >= 20) {
                    setState({ status: "slow", tries });
                    return;
                }
                setTimeout(poll, 2000);
                setState({ status: "polling", tries });
            } catch (err) {
                if (!cancelled) setState({ status: "error", detail: formatApiError(err) });
            }
        };
        poll();
        return () => { cancelled = true; };
    }, [sp]);

    return (
        <div className="min-h-screen flex items-center justify-center bg-stone-50 p-6" data-testid="billing-success">
            <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
                {state.status === "polling" && (
                    <>
                        <Loader2 size={40} className="mx-auto text-stone-500 animate-spin mb-4" />
                        <h1 className="font-display text-3xl text-stone-900">Confirming your payment…</h1>
                        <p className="text-sm text-stone-500 mt-3">
                            Attempt {state.tries} · this usually takes a couple of seconds.
                        </p>
                    </>
                )}
                {state.status === "paid" && (
                    <>
                        <CheckCircle2 size={48} className="mx-auto text-emerald-500 mb-4" data-testid="billing-paid" />
                        <h1 className="font-display text-3xl text-stone-900">Payment received.</h1>
                        <p className="text-sm text-stone-500 mt-3">
                            Your workspace has been upgraded. You&apos;re all set.
                        </p>
                        <button
                            onClick={() => nav("/", { replace: true })}
                            className="mt-6 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-stone-900 text-white text-sm font-semibold hover:bg-stone-800"
                            data-testid="billing-continue"
                        >
                            Go to Command Centre <ArrowRight size={14} />
                        </button>
                    </>
                )}
                {(state.status === "failed" || state.status === "error") && (
                    <>
                        <XCircle size={40} className="mx-auto text-rose-500 mb-4" data-testid="billing-failed" />
                        <h1 className="font-display text-3xl text-stone-900">
                            {state.status === "failed" ? "Payment did not go through." : "Something went wrong."}
                        </h1>
                        <p className="text-sm text-stone-500 mt-3">
                            {state.detail || "Please try again or reach out to support."}
                        </p>
                        <Link to="/pricing" className="mt-6 inline-flex items-center gap-1 text-sm font-medium text-stone-900 underline">
                            Back to pricing <ArrowRight size={12} />
                        </Link>
                    </>
                )}
                {state.status === "slow" && (
                    <>
                        <Loader2 size={40} className="mx-auto text-amber-500 mb-4" />
                        <h1 className="font-display text-3xl text-stone-900">Still confirming…</h1>
                        <p className="text-sm text-stone-500 mt-3">
                            Your payment is being processed. We&apos;ll email you once it&apos;s confirmed.
                        </p>
                        <button onClick={() => nav("/", { replace: true })} className="mt-6 text-sm font-medium underline">Continue anyway</button>
                    </>
                )}
            </div>
        </div>
    );
}
